import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrgMemberPicker } from '@/features/org/components/org-member-picker/org-member-picker';
import { useOrgMembersQuery } from '@/features/org/hooks/use-org-members-query';
import { DefaultMemberInvitationList } from '@/features/projects/create-project/components/default-member-invitation-list';
import { ProjectMemberInvitationList } from '@/features/projects/create-project/components/project-member-invitation-list';
import { useProjectAccessors } from '@/features/projects/shared/hooks/use-project-accessors';
import { useProjectMemberInvitesQuery } from '@/features/projects/shared/hooks/use-project-member-invites-query';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';

interface ProjectMemberPickerDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  onSendInvitations: (newInvites: string[]) => void;
}

export const ProjectMemberPickerDialog = ({
  projectId,
  open,
  onOpenChange,
  isLoading,
  onSendInvitations,
}: ProjectMemberPickerDialogProps) => {
  const { t } = useTranslation('project', { keyPrefix: 'MemberManagement' });
  const [newInvites, setNewInvites] = useState<Set<string>>(new Set());

  const { data: orgMembers } = useOrgMembersQuery();
  const {
    data: invites,
    isLoading: isInvitesLoading,
    error: invitesError,
  } = useProjectMemberInvitesQuery({
    projectId: projectId!,
    statuses: ['pending', 'declined'],
  });
  const existingMembers = useProjectAccessors(projectId);

  useEffect(() => {
    if (open) {
      setNewInvites(new Set());
    }
  }, [open]);

  const handleAdd = (memberId: string) => {
    setNewInvites((prev) => {
      prev.add(memberId);
      return new Set(prev);
    });
  };

  return isInvitesLoading ? (
    <LoadingIndicator />
  ) : invitesError || !invites ? (
    <span>{t('could_not_fetch_invites')}</span>
  ) : (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeInline>
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            {t('add_member_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-8 flex flex-col gap-8">
          <div>
            <OrgMemberPicker
              orgMembers={orgMembers}
              existingInvitations={[
                ...invites.map((invite) => invite.org_member_id),
                ...Array.from(newInvites),
              ]}
              onSuccess={handleAdd}
              onlyMemberRole={true}
            />
          </div>

          {existingMembers.length > 0 && (
            <DefaultMemberInvitationList
              members={existingMembers.map((member) => ({
                ...member.orgMember,
                profile: member.orgMember.profile,
              }))}
            />
          )}
          {newInvites.size > 0 && (
            <ProjectMemberInvitationList
              memberIdsToInvite={Array.from(newInvites)}
              orgMembers={orgMembers || []}
              onRemove={(id) =>
                setNewInvites((prev) => {
                  prev.delete(id);
                  return new Set(prev);
                })
              }
            />
          )}
        </div>

        <DialogFooter className="mt-14 sm:justify-center">
          <Button
            className="min-w-40"
            type="button"
            variant="greenGradient"
            onClick={() => onSendInvitations(Array.from(newInvites))}
            disabled={newInvites.size === 0 || isLoading}
          >
            {t('submit_button_label')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
